import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SignInFacade } from '../../application/sign-in-facade';
import {
  CenteredContainerComponent
} from '@shared/ui/components/layout/centered-container/centered-container.component';
import { CardComponent } from '@shared/ui/components/card/card.component';
import { TenantLogoComponent } from '@shared/ui/components/tenant-logo/tenant-logo.component';
import { SignInFormComponent, } from '../components/sign-in-password-form.component';
import { ErrorMessageComponent } from '@shared/ui/components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '@shared/ui/components/loading-spinner/loading-spinner.component';
import { LegalFooterComponent } from '@shared/ui/components/legal-footer/legal-footer.component';
import { LoginCredentials } from '@core/auth';
import { SignInMethodSelectComponent } from '@features/auth/presentation/components/sign-in-method-select.component';
import { ClockService } from '@core/clock/clock.service';

/**
 * Sign-In Page
 *
 * Landing page for user authentication.
 * Displays tenant branding and sign-in form.
 */

@Component({
  selector: 'app-sign-in-page',
  standalone: true,
  imports: [
    CommonModule,
    CenteredContainerComponent,
    CardComponent,
    TenantLogoComponent,
    ErrorMessageComponent,
    LoadingSpinnerComponent,
    LegalFooterComponent,
    SignInMethodSelectComponent,
    SignInFormComponent,
  ],
  providers: [SignInFacade], // Feature-level facade
  template: `
    @let vm = this.vm();
    <app-centered-container [maxWidth]="780">
      @if (vm.tenantLoading) {
        <app-loading-spinner></app-loading-spinner>
      } @else {
        <app-card>
          <div class="header">
            <app-tenant-logo
              logoUrl="/assets/images/ross-robotics-logo.svg"
              tenantName=""
              align="start"
              altText="Company Logo"
            ></app-tenant-logo>
            <div class="header-right">
              <span class="divider"></span>
              <div class="title-container">
                <p class="title">AIDA</p>
                <p class="subtitle">ROSS ROBOTICS</p>
              </div>
            </div>
          </div>
          @if (vm.errorMessage) {
            <app-error-message [message]="vm.errorMessage"></app-error-message>
          }

          <div class="content">
            @if (vm.authStep === 'select') {
              <app-sign-in-method-select
                (selectMethod)="onMethodSelect($event)"
              ></app-sign-in-method-select>
            } @else {
              <app-sign-in-password-form
                [isSubmitting]="vm.isSubmitting"
                [showPasswordField]="vm.authStep === 'password'"
                (formSubmit)="onSubmit($event)"
                (onFieldChange)="onFieldChange()"
                (goBack)="resetToSelection()"
              ></app-sign-in-password-form>
            }
          </div>

          <div class="support-text">
            <span>Need an account? Contact</span>
            <a href="mailto:support@ross-robotics.co.uk" class="support-link"> Support</a>
          </div>

          <app-legal-footer
            class="page-legal-footer"
            companyName="Ross Robotics"
            [year]="currentYear"
          ></app-legal-footer>
        </app-card>
      }
    </app-centered-container>
  `,
  styles: [
    `
      :host {
        min-width: 350px;
      }
      .content {
        margin: var(--aida-ref-spacing-400) 0;
      }
      .support-text {
        padding: var(--spacing-sm);
        font-size: var(--aida-ref-font-size-400);
        color: var(--aida-colour-text-tertiary);
        font-family: var(--font-family-base), sans-serif;
        text-align: center;
        margin-bottom: var(--aida-ref-spacing-125);
      }

      .support-link {
        color: var(--aida-ref-colour-blue-400);
        font-weight: var(--aida-ref-font-weight-semibold);
        text-decoration: none;
      }

      .support-link:hover {
        text-decoration: underline;
      }

      .header {
        display: flex;
        gap: var(--aida-ref-spacing-125);
        align-items: center;
      }

      .header-right {
        display: flex;
        flex-direction: row;
        gap: var(--aida-ref-spacing-125);
      }

      .title-container {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }

      .divider {
        border-right: var(--aida-colour-outline-default) var(--aida-ref-border-width-medium) solid;
      }

      .title {
        font-size: var(--aida-ref-font-size-700);
        font-weight: var(--aida-ref-font-weight-bold);
        color: var(--aida-colour-text-primary);
        font-family: var(--aida-ref-font-family-text);
      }

      .subtitle {
        font-size: var(--aida-ref-font-size-600);
        font-weight: var(--aida-ref-font-weight-regular);
        color: var(--aida-colour-text-primary);
        font-family: var(--aida-ref-font-family-text);
      }

      @media (max-width: 600px) {
        .title {
          font-size: var(--aida-ref-font-size-500);
        }

        .subtitle {
          font-size: var(--aida-ref-font-size-300);
        }
      }
    `,
  ],
})
export class SignInPageComponent {
  private readonly facade = inject(SignInFacade);
  private readonly clock = inject(ClockService);

  protected readonly currentYear = this.clock.tick().getFullYear();

  readonly vm = this.facade.vm;

  protected onMethodSelect(method: 'sso' | 'password'): void {
    this.facade.clearError();
    this.facade.setAuthStep(method);
  }

  protected resetToSelection(): void {
    this.facade.clearError();
    this.facade.setAuthStep('select');
  }

  protected onPasswordSubmit(credentials: LoginCredentials): void {
    void this.facade.submitAuthentication(credentials, this.facade.returnUrl());
  }

  protected onSsoSubmit(creds: LoginCredentials): void {
    this.facade.submitSsoAuthentication(creds);
  }

  protected onSubmit(creds: LoginCredentials): void {
    if (creds.password) {
      void this.onPasswordSubmit(creds);
    } else {
      this.onSsoSubmit(creds);
    }
  }

  protected onFieldChange(): void {
    this.facade.clearError();
  }
}
